import React, { useState, useEffect } from 'react';

interface InstallPromptProps {
  onInstall?: () => void;
}

const InstallPrompt: React.FC<InstallPromptProps> = ({ onInstall }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(ios);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Listen for beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      if (isIOS) {
        setShowIOSInstructions(true);
      }
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      onInstall?.();
    }

    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  if (!showInstallButton && !isIOS) {
    return null;
  }

  return (
    <>
      {showInstallButton && (
        <div style={styles.banner}>
          <div style={styles.bannerContent}>
            <span>📱 Install ShahoSuite for quick access!</span>
            <button onClick={handleInstallClick} style={styles.installButton}>
              Install App
            </button>
            <button onClick={() => setShowInstallButton(false)} style={styles.dismissButton}>
              ✕
            </button>
          </div>
        </div>
      )}

      {isIOS && !window.matchMedia('(display-mode: standalone)').matches && (
        <div style={styles.iosHint}>
          <button onClick={() => setShowIOSInstructions(true)} style={styles.iosButton}>
            📱 Add to Home Screen
          </button>
        </div>
      )}

      {showIOSInstructions && (
        <div style={styles.modal} onClick={() => setShowIOSInstructions(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3>Install ShahoSuite on iOS</h3>
            <ol style={{ textAlign: 'left', lineHeight: 1.8 }}>
              <li>Tap the <strong>Share</strong> button <span style={{ fontSize: 20 }}>⬆️</span> at the bottom of Safari</li>
              <li>Scroll down and tap <strong>"Add to Home Screen"</strong> 📱</li>
              <li>Tap <strong>"Add"</strong> in the top right</li>
              <li>The ShahoSuite app icon will appear on your home screen!</li>
            </ol>
            <button onClick={() => setShowIOSInstructions(false)} style={styles.closeButton}>
              Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const styles: any = {
  banner: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: '#0074D9',
    color: '#fff',
    padding: '12px 16px',
    boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
    zIndex: 9999,
    animation: 'slideUp 0.3s ease'
  },
  bannerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: 1200,
    margin: '0 auto',
    gap: 12
  },
  installButton: {
    background: '#fff',
    color: '#0074D9',
    border: 'none',
    padding: '8px 20px',
    borderRadius: 6,
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 14
  },
  dismissButton: {
    background: 'transparent',
    border: 'none',
    color: '#fff',
    fontSize: 20,
    cursor: 'pointer',
    padding: 4
  },
  iosHint: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    zIndex: 9999
  },
  iosButton: {
    background: '#0074D9',
    color: '#fff',
    border: 'none',
    padding: '12px 20px',
    borderRadius: 25,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0,116,217,0.4)',
    fontSize: 14
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: 20
  },
  modalContent: {
    background: '#fff',
    padding: 30,
    borderRadius: 12,
    maxWidth: 400,
    width: '100%',
    textAlign: 'center'
  },
  closeButton: {
    background: '#0074D9',
    color: '#fff',
    border: 'none',
    padding: '10px 24px',
    borderRadius: 6,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 20,
    fontSize: 14
  }
};

export default InstallPrompt;
